<template>
	<form v-on:submit.prevent="saveExpense">
		<h2>{{ title ? title : 'Log Expense'}}</h2>

		<div class="row mb-4">
			<div class="col-lg-6">
				<form-group label="Receipt Image" col-class="col-md-4" :invalid="$store.errorList.image_id">

					<upload-field-with-panel
						:value="uploadImage"
						api-model="ReceiptImage"
						upload-type="image/pdf"
						help-message="Ensure the image is of the itemized receipt, NOT the point-of-sale slip"
						v-on:uploaded="imageUploaded">
					</upload-field-with-panel>
					
				</form-group>

		<div class="row">
		  <div class="col-md-4"></div>
		  <div class="col-md-8">
			<button
				type="button"
				class="btn btn-primary d-flex justify-content-center align-items-center w-100 mb-3"
				:disabled="!expense.image_id || predictionFetched"
				v-on:click="predictFile">
					<i class="fa fa-magic mr-2"></i>
					Auto-Fill with AI
			</button>
		  </div>
		</div>

	  </div>
			<div class="col-lg-6">
				<field label="Date of Purchase" 
					col-class="col-md-4" 
					:params="{type: 'date'}" 
					v-model="expense.purchase_date"
					:invalid="$store.errorList.purchase_date">
				</field>

				<field label="Payment Type" 
					col-class="col-md-4" 
					:params="{type: 'select', options: expensePaymentTypeOptions}"
					v-model="expense.payment_method"
					:invalid="$store.errorList.payment_method"
					v-on:change="paymentTypeChange">
				</field>

				<field v-if="expense.payment_method == 'Business Card' && qboIsActive"
					label="Credit Card"
					col-class="col-6"
					:params="{type:'select',options:ccAccountOptions,required:true}"
					v-model="expense.qbo_account_id"
					:invalid="$store.qbo_account_id">
				</field>

				<field v-else-if="expense.payment_method == 'Business Card'" 
					label="Last 4 Card Digits" 
					col-class="col-6" 
					:params="{type: 'text',required:true}"
					v-model="expense.last_4"
					:invalid="$store.errorList.last_4">	
				</field>
			</div>
		</div>

		<hr>

		<field label="Where was the purchase made from?" 
			col-class="col-md-4" 
			v-model="expense.vendor"
			:invalid="$store.errorList.vendor">	
		</field>

		<field label="Vendor Invoice #" 
			col-class="col-md-4" 
			v-model="expense.invoice_num"
			:invalid="$store.errorList.invoice_num">
		</field>

		<hr>

		<div class="expense-line-horizontal">
			<table-list record-name="Expense Line Item"
				:fields="[
					{label: 'Project/Service',key:'jobLabel'},
					{label: 'Expense Group/Code',key:'groupAndCode'},
					{label: 'Description',key:'description'},
					{label: 'Amount',key:'costDisplay',align:'right'},
					{label: 'Tip',key:'tipDisplay',align:'right'},
					{label: 'Tax',key:'taxDisplay',align:'right'},
					{label: 'Line Total',key:'lineTotalDisplay',align:'right'},
				]"
				:records="mutatedLineItems"
				:has-edit="false"
				:has-delete="true"
				:other-buttons="[
					{
						text: 'Edit',
						icon: 'fa-pencil-alt',
						condition: ['isInactive',true],
						title: 'Edit line item',
						click: 'editLineItem',
						class: 'btn-light'
					}
				]"
				:hide-record-count="true"
				:simple-delete="true"
				v-on:editLineItem="editLineItemFromTableList"
				v-on:delete="deleteLineItemFromTableList">
			</table-list>

		</div>


		<div v-for="(line,index) in lineItems" class="card mb-2 expense-line-item-card" 
			:class="{
				'expense-line-item-card-active': index == lineEditIndex,
				'expense-line-error': 'invalidKeys' in line && line.invalidKeys.length > 0
			}">
			<div v-if="lineItems.length > 1" class="card-delete-button text-right pr-2 pt-2">
				<i class="fa fa-trash icon-button" v-on:click="removeLineItem(index)"></i>
			</div>
			<div v-if="lineEditIndex === index" class="card-body">

				<job-picker
					v-if="!lineItems[index].service_id" 
					col-class="col-md-4" 
					v-model="lineItems[index].job_id"
					job-type="project"
					:key="'project_picker'+index"
					:invalid="$store.errorList.job_id"
					v-on:change="jobChange">
				</job-picker>

				<job-picker
					v-if="!lineItems[index].job_id" 
					col-class="col-md-4" 
					v-model="lineItems[index].service_id"
					job-type="service"
					:key="'service_picker'+index"
					:invalid="$store.errorList.service_id"
					v-on:change="jobChange">
				</job-picker>

				<field label="Expense Group" 	
					col-class="col-md-4" 
					:params="{type: 'select', options: expenseGroupOptions}" 
					v-on:change="groupSelected" 
					v-model="lineItems[index].group_id"
					:invalid="$store.errorList.group_id">
				</field>

				<field v-if="lineItems[index].group_id > 0" 
					label="Expense Code" 
					col-class="col-md-4" 
					:params="{type: 'select', options: expenseCodesFilteredOptions}" 
					v-model="lineItems[index].code_id"
					v-on:change="codeSelected(index,$event)"
					:invalid="$store.errorList.code_id">	
				</field>

				<field label="What was it?" 
					col-class="col-md-4" 
					v-model="lineItems[index].description"
					:invalid="$store.errorList.description">
				</field>

				<div class="mb-2 float-right">
					<button v-if="!line.hasTip" class="btn btn-success" type="button" v-on:click="line.hasTip = true">
						<i class="fa fa-thumbs-up"></i>
						Has Tip
					</button>

					<button v-if="!line.hasTax && !qboIsActive" class="btn btn-success" type="button" v-on:click="line.hasTax = true">
						<i class="fa fa-percent"></i>
						Has Tax
					</button>
				</div>
				<div style="clear: both;"></div>

				<form-group :label="line.hasTax || line.hasTip ? 'Line Subtotal $' : 'Line Total $'" col-class="col-md-4">
					<input class="form-control" type="number" step="0.01" v-model="lineItems[index].cost" v-on:change="lineCostChange($event,index)" style="padding: 5px;font-size:24pt;text-align: center;box-shadow: inset 0 0 10px #e0e0e0">
				</form-group>

				<field v-if="line.hasTip"
					label="Tip Amount"
					col-class="col-5 col-md-4"
					:params="{type:'number',step:'0.01'}"
					v-model="lineItems[index].tip">
				</field>

				<template v-if="line.hasTax || qboIsActive">

					<field v-if="qboIsActive"
						label="Tax Paid"
						col-class="col-md-4"
						:params="{type:'select',options:qboSalesTaxOptions}"
						v-model="lineItems[index].qbo_tax_id"
						v-on:change="setLineTaxRate($event,index)">
					</field>

					<field v-else label="Tax Paid"
						col-class="col-5 col-md-4" 
						:params="{type:'number',step:'0.01'}" 
						v-model="lineItems[index].tax">		
					</field>

				</template>

				<form-group v-if="line.hasTax || line.hasTip"  label="" col-class="col-md-4 col-12">
					<div class="expense-total">
						<div class="float-left">Line Total</div>
						<div class="total-amount">$ {{ getLineSubtotal(line).toCurrency() }}</div>
					</div>
				</form-group>
			</div>
			<div v-else class="card-body expense-line-item-static">
				<table class="expense-line-table table table-sm table-striped" v-on:click="lineEditIndex = index">
					<tbody>
						<tr>
							<th>
								{{ line.job_id > 0 ? 'Project' : '' }}
								{{ line.service_id > 0 ? 'Service' : '' }}

							</th>
							<td>
								<job-picker v-if="line.job_id > 0"
									:readonly="true"
									:hide-label="true"
									job-type="project"
									:value="line.job_id"
									key="project"
									v-on:change="jobChange">
								</job-picker>
								<job-picker v-if="line.service_id > 0"
									:readonly="true"
									:hide-label="true"
									job-type="service"
									key="service"
									:value="line.service_id"
									v-on:change="jobChange">
								</job-picker>
								
							</td>
						</tr>
						<tr>
							<th>Expense Group</th>
							<td>{{ getGroupName(line.group_id) }}</td>
						</tr>
						<tr>
							<th>Expense Code</th>
							<td>{{ line.codeName }}</td>
						</tr>
						<tr>
							<th>Description</th>
							<td>{{ line.description }} </td>
						</tr>
						<tr>
							<th>Amount</th>
							<td class="text-right">{{ line.cost.toCurrency() }}</td>
						</tr>
						<tr>
							<th>Tip</th>
							<td class="text-right">{{ line.tip.toCurrency() }}</td>
						</tr>
						<tr>
							<th>Tax</th>
							<td class="text-right">{{ line.tax.toCurrency() }}</td>
						</tr>
						<tr>
							<th>Line Total</th>
							<td style="border-top: 2px solid black;" class="text-right"><strong>{{ getLineSubtotal(line).toCurrency() }}</strong></td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>

		<div style="clear: both;"></div>

		<button type="button" class="btn btn-dark float-right mb-2" v-on:click="addLineItem">
			<i class="fa fa-plus"></i>
			Add Item
		</button>

		<div style="clear: both;"></div>

		<div class="expense-total mb-2" style="width: 100%;">
			<div class="float-left">Total</div>
			<div class="total-amount">$ {{ getLineItemsTotal(lineItems).toCurrency() }}</div>
		</div>

		<alert :visible="showValidationError"
			classes="alert-danger"
			icon="fa-warning"
			message="Please review your submission, as there are some fields that don't look quite right"
			v-on:close="showValidationError = false">
		</alert>

		<button type="submit" class="btn btn-primary btn-lg" style="display: block;width: 100%">
			<i class="fa fa-paper-plane"></i>
			Post Expense
		</button>

		<p class="text-muted">Tradetraks does not verify the accuracy of yours, or AI generated inputs. Please ensure all information is correct.</p>

	</form>
</template>
<script type="text/javascript">
import FormGroup from "../FormGroup"
import Field from "../Field"
import ModalDialog from "../ModalDialog"
import UploadFieldWithPanel from "@/components/super_components/UploadFieldWithPanel"
import TableList from "@/components/TableList"
import Alert from "@/components/Alert"
import JobPicker from "@/components/super_components/JobPicker"

import ExpenseMixins from "@/mixins/ExpenseMixins.js"
import moment from "moment"

export default {
	name: "ExpenseLogForm",
	components: {
		ModalDialog,
		FormGroup,
		Field,
		UploadFieldWithPanel,
		TableList,
		Alert,
		JobPicker
	},
	mixins: [ExpenseMixins],
	props: [
		"title",
		"expenseData"
	],
	data() {
		return {
			lineEditIndex: 0,
			expenseCodesFilteredOptions: [],
			lineItems: [
				{
					job_id: null,
					service_id: null,
					group_id: null,
					code_id: null,
					codeName: "",
					description: null,
					cost: 0,
					hasTax: false,
					hasTip: false,
		  tax: 0,
		  tip: 0,
		  qbo_tax_id: null,
					qboTaxRate: null
				}
			],
			expense: {
				user_id: this.$store.userDetails.id,
				image_id: null,
				vendor: null,
				payment_method: null,
				last_4: null,
				purchase_date: null
			},
			uploadImage: 0,
			globalKeys: ["image_id","vendor","payment_method","last_4","purchase_date","qbo_account_id","invoice_num"],
			lineItemKeys: ["job_id","service_id","group_id","code_id","cost","tip","tax","description","qbo_tax_id"],
			payloadIsValid: false,
			showValidationError: false,

	  predictionFetched: false
		}
	},
	watch: {
		expenseData() {
			this.setDataFromProps()
		},
		qboIsActive() {
			if (this.qboIsActive)
				this.fetchQboSalesTaxes()
		}
	},
	computed: {

		showSubmitButton() {

			if ((this.expense.job_id || this.expense.service_id) && 
				this.expense.code_id && 
				this.expense.group_id && 
				this.expense.image_id && 
				this.expense.cost > 0 && 
				this.expense.vendor !="" && 
				this.expense.purchase_date != "")

				return true
			else
				return false
		},
		mutatedLineItems() {
			let vm = this
			return this.lineItems.map((line,index) => {
				let type = "project"
				let key = "job_id"
				if (line.service_id > 0) {
					type = "service"
					key = "service_id"
				}

				line.groupAndCode = vm.getGroupName(line.group_id)+": "+line.codeName

				line.taxDisplay = line.tax.toCurrency()
				line.costDisplay = line.cost.toCurrency()
				line.tipDisplay = line.tip.toCurrency()
				line.lineTotalDisplay = vm.getLineSubtotal(line).toCurrency()

				if (vm.qboIsActive) {
					let taxMultiple = line.qboTaxRate/100

					let tax = +line.cost * taxMultiple
					line.taxDisplay = tax.toCurrency()
				}

				line.isInactive = index != vm.lineEditIndex

				line.rowClasses = ""
				if (!line.isInactive)
					line.rowClasses = "bg-light border font-weight-bold"

				if ("invalidKeys" in line && line.invalidKeys.length > 0) {
					line.rowClasses += " text-danger"
				}

				return line
			})
		}
	},
	methods: {

		mapPredictionToExpense(pred) {

			// Purchase date
			this.expense.purchase_date = null
			try {
				const purchaseDate = moment.utc(pred.purchase_datetime).format("YYYY-MM-DD");
				this.expense.purchase_date = purchaseDate
			} catch {}
			this.$store.errorList.purchase_date = !Boolean(this.expense.purchase_date)

			// Vendor
			this.expense.vendor = ('vendor' in pred) ? pred.vendor : ''
			this.$store.errorList.vendor = !Boolean(this.expense.vendor)

			// Invoice number
			this.expense.invoice_num = ('transaction_number' in pred) ? pred.transaction_number : ''
			this.$store.errorList.invoice_num = !Boolean(this.expense.invoice_num)

			// Payment method
			this.expense.payment_method = ('payment_method' in pred) ? pred.payment_method : null
			this.$store.errorList.payment_method = !Boolean(this.expense.payment_method)

			// Last 4 digits (if applicable)
			this.expense.last_4 = null
			if (this.expense.payment_method == 'Business Card') {
				if ('last_four_digits' in pred && pred.last_four_digits)  // note: fields can be null
					this.expense.last_4 = pred.last_four_digits
				else
					this.$store.errorList.last_4 = true   // Mark field as requiring input
			}

			// Expense Code
			const expenseCodeId = ('expense_code_id' in pred) ? Number(pred.expense_code_id) : null
			this.$store.errorList.code_id = !Boolean(expenseCodeId)

			// Expense Group
			const expenseGroupId = ('expense_group_id' in pred) ? Number(pred.expense_group_id) : null
			this.$store.errorList.group_id = !Boolean(expenseGroupId)
			
			const description = ('summary' in pred) ? pred.summary : ''
			const subtotal = ('subtotal' in pred) ? pred.subtotal.round(2) : 0
			const tax = ('tax_paid' in pred) ? pred.tax_paid.round(2) : 0
			const tip = ('tip_paid' in pred) ? pred.tip_paid.round(2) : 0

			this.lineItems = [
				{
					jobLabel: "",
					job_id: null,      // may be parsed later
					service_id: null,  // may be parsed later
					group_id: expenseGroupId,
					code_id: expenseCodeId,
					codeName: "",      // updated later with codeSelected
					description: description,
					cost: subtotal,
					hasTax: (tax > 0),
					hasTip: (tip > 0),
					tax: tax,
					tip: tip,
					qbo_tax_id: null,  // never parsed
					qboTaxRate: null   // never parsed
				}
			]

			// Must be called after lineItems is set
			this.groupSelected(expenseGroupId).then(() => {
				this.codeSelected(0, expenseCodeId)
			})
		},


		predictJobService(purchase_datetime_str) {
			
			let purchaseDatetime;
			try {
				// Ensure string is a valid datetime
				purchaseDatetime = moment(purchase_datetime_str).utc().format("YYYY-MM-DD HH:mm:ss");

			} catch (e) {
				// Unable to parse -- mark fields for manual
				this.$store.errorList.job_id = true
				this.$store.errorList.service_id = true
				this.spinner(false)
				return
			}
			
			const payload = {
				fields: ['job_id', 'service_id'],
				filters: [
					['user_id', this.$store.userDetails.id],
					['time_in <=', purchaseDatetime],
					['time_out >=', purchaseDatetime],

					['user_id', this.$store.userDetails.id, 'or'],
					['time_in <=', purchaseDatetime],
					['time_out', '0000-00-00 00:00:00'],
					
				],
				limit: 1,
				include: [
					{ model: 'Job', fields: ['job_num', 'job_name'] },
					{ model: 'Service', fields: ['job_num', 'job_name'] }
				],
				orders: [
					["id", "DESC"]  // get newest first
				]
			}

			this.getJSON("TimeLog/get", payload).then(response => {
				if (response.success && response.data.length) {

					const timelog = response.data[0]

					let jobOrService = null;
					if ('Job' in timelog && timelog.Job) 
						jobOrService = timelog.Job 
					else if ('Service' in timelog && timelog.Service) 
						jobOrService = timelog.Service

					const jobLabel = (jobOrService) ? jobOrService.job_num + ": " + jobOrService.job_name : ""

					this.lineItems[0].job_id = Number(timelog.job_id)
					this.lineItems[0].service_id = Number(timelog.service_id)
					this.lineItems[0].jobLabel = jobLabel

				} else {
					this.$store.errorList.job_id = true
					this.$store.errorList.service_id = true
				}
				this.spinner(false)
			})

		},

		predictFile() {
			this.predictionFetched = true
			this.spinner(true)
			const payload = { id: this.expense.image_id }

			this.postData("ReceiptImage/predict", payload, true).then(response => {
				if (response.success) {
					if ('purchase_datetime' in response.data && response.data.purchase_datetime)
						this.predictJobService(response.data.purchase_datetime)
					else
						this.spinner(false)

					this.mapPredictionToExpense(response.data)
				} else {
					this.predictionFetched = false
					this.spinner(false)
				}
			})
		},

		validateValues() {
			let vm = this

			let requiredLineFields = ["group_id","code_id","cost"]
			let requiredGlobalFields = ["image_id","purchase_date","payment_method"]

			let invalidGlobalFields = []

			this.payloadIsValid = true
			requiredGlobalFields.forEach(key => {
				if (!vm.expense[key]) {
					vm.payloadIsValid = false
					vm.$store.errorList[key] = true
				}
				else
					vm.$store.errorList[key] = false
			})

			//special extra rules
			if (vm.moment(vm.expense.purchase_date).isAfter(vm.moment())) {
				vm.payloadIsValid = false
				vm.$store.errorList.purchase_date = true
			}

			this.lineItems.forEach(line => {
				line.invalidKeys = []
				requiredLineFields.forEach(key => {
					if (!line[key]) {
						vm.payloadIsValid = false
						line.invalidKeys.push(key)
						line.rowClasses += " text-danger"
					}
				})
			})
		},
		getGroupName(id) {
			let group = this.expenseGroupOptions.find(opt => opt.value == id)

			if (group)
				return group.text

			return ""
		},
		setDataFromProps() {
			for (let key in this.expenseData) 
				this.expense[key] = this.expenseData[key]
		},
		lineCostChange(value,index) {
			this.setLineTaxAmount(index)
		},
		jobChange(value) {
			this.$forceUpdate()
		},
		setLineTaxRate(value,index) {
			let vm = this
			this.getQboTaxRate(value).then(qboTaxRate => {
				vm.lineItems[index].qboTaxRate = qboTaxRate

				vm.setLineTaxAmount(index)
				vm.$forceUpdate()
			})
		},
		setLineTaxAmount(index) {
			console.log("setLineTaxAmount", this.lineItems[index])
			if ("qboTaxRate" in this.lineItems[index] && this.lineItems[index].qboTaxRate) {
				let taxAmount = +this.lineItems[index].qboTaxRate * +this.lineItems[index].cost / 100

				this.lineItems[index].tax = taxAmount.round(2)
			}
		},
		getLineSubtotal(line) {
			let total = 0

			if (this.qboIsActive ) {
				let taxMultiple = 1 + (line.qboTaxRate/100)

				total = +line.cost * taxMultiple
			}
			else {
				if ("cost" in line && line.cost)
					total += +line.cost

				if ("tax" in line && line.tax)
					total += +line.tax
			}

			if ("tip" in line && line.tip)
				total += +line.tip

			if (isNaN(total))
				return "0.00"
			else
				return total
		},
		getLineItemsTotal(lineItems) {
			return lineItems.reduce((acc,line) => acc + +this.getLineSubtotal(line),0)
		},
		paymentTypeChange(value) {
			let vm = this
			if (this.qboIsActive && value == 'Business Card') {
				this.fetchQBOResource("Account","AccountType='Credit Card'",false).then(response => {
					if (response.success)
						vm.$store.qboCreditCardAccounts = response.data
				})
				if (!this.$store.qboUserCreditCardSettings || this.$store.qboUserCreditCardSettings.length == 0)
					this.fetchQBOCreditCardAccountsUserSettings()
			}
			vm.$forceUpdate()
		},
		groupSelected(group_id) {

			let filters = JSON.stringify([
				['group_id',group_id]
			]);

			let vm = this
			return this.getJSON("ExpenseCode/get?filters="+filters).then(response => {

				let expenseCodesFiltered = response.data.map(code => {
					code.display = code.code + ": " + code.description

					return code
				})

				vm.expenseCodesFilteredOptions = vm.optionify(expenseCodesFiltered,"display")
			})
		},
		codeSelected(lineIndex,code_id) {
			this.lineItems[lineIndex].codeName = ""
			let code = this.expenseCodesFilteredOptions.find(opt => opt.value == code_id)
			if (code)
				this.lineItems[lineIndex].codeName = code.text
		},
		addLineItem() {

			//get job name

			let lastIndex = this.lineItems.length - 1

			let endpoint = "Service", jobId = null

			if (this.lineItems[lastIndex].job_id > 0) {
				endpoint = "Job"
				jobId = this.lineItems[lastIndex].job_id
			}
			else if (this.lineItems[lastIndex].service_id > 0) {
				endpoint = "Service"
				jobId = this.lineItems[lastIndex].service_id
			}
			
			let vm = this

			this.getJSON(endpoint + "/find/" + jobId).then(response => {
				if (response.success) {
					vm.lineItems.push({
						jobLabel: response.data.job_num + ": " + response.data.job_name,
						job_id: vm.lineItems[lastIndex].job_id,
						service_id: vm.lineItems[lastIndex].service_id,
						group_id: vm.lineItems[lastIndex].group_id,
						code_id: vm.lineItems[lastIndex].code_id,
						codeName: vm.lineItems[lastIndex].codeName,
						description: null,
						cost: 0,
						tip: 0,
						tax: 0,
						hasTax: false,
						hasTip: false
					})

					vm.lineEditIndex = vm.lineItems.length - 1
				}
			})
		},
		removeLineItem(index) {
			if (this.lineItems.length > 1) //do not allow removal of last item
				this.lineItems.splice(index,1)
		},
		editLineItemFromTableList(record,index) {
			this.lineEditIndex = index
		},
		deleteLineItemFromTableList(record,index) {
			this.removeLineItem(index)
		},
		imageUploaded(response) {
			this.expense.image_id = response.data.id
	  this.predictionFetched = false
		},
		saveExpense() {
			let vm = this

			this.validateValues()

			if (this.payloadIsValid) {

				if (this.qboIsActive)
					//calculate tax amount
					this.expense.tax = +this.expense.cost * this.qboTaxRate/100

				let expensesPayload = []
				this.lineItems.forEach(line => {

					let compiledExpense = {
						user_id: vm.$store.userDetails.id,
					}

					vm.globalKeys.forEach(key =>{
						compiledExpense[key] = vm.expense[key]
					})
					vm.lineItemKeys.forEach(key => {
						compiledExpense[key] = line[key]
					})

					expensesPayload.push(compiledExpense)
				})

				let saves = []

				expensesPayload.forEach(payload => {
					saves.push(vm.postData("Expense/save",payload,true))
				})

				Promise.all(saves).then(responses => {
					let failed = []
					responses.forEach(response => {
						if (!response.success) 
							failed.push(response)
					})

					if (failed.length == 0) {
						vm.alertSaved("Expense Posted!")
						vm.$emit("expensePosted",vm.expense)
						vm.showValidationError = false
						vm.uploadImage++

						vm.lineItems = [
							{
								job_id: null,
								service_id: null,
								group_id: null,
								code_id: null,
								codeName: "",
								description: null,
								cost: 0,
								tip: 0,
								tax: 0,
								qbo_tax_id: null,
								hasTax: false,
								hasTip: false
							}
						]

						vm.expense = {
							user_id: this.$store.userDetails.id,
						}

						vm.globalKeys.forEach(key => {
							vm.expense[key] = null
						})
					}
				})
			}
			else {
				this.showValidationError = true
			}

			/*this.saveRecord("Expense",this.expense, response => {
				
				vm.alertSaved("Expense Posted!")
				vm.$emit("expensePosted",vm.expense)

				vm.expense = {
					user_id: this.$store.userDetails.id,
					job_id: null,
					service_id: null,
					group_id: null,
					code_id: null,
					cost: 0,
					tip: 0,
					tax: 0,
					image_id: null,
					description: null,
					vendor: null,
					payment_method: null,
					last_4: null,
					qbo_account_id: null,
					purchase_date: null,
				}

				vm.uploadImage++
			})*/
		},
		fetchData() {
			this.spinner(true)

			let promises = [
				this.fetchModel('ExpenseGroup')
			]

			if (this.qboIsActive)
				this.fetchQboSalesTaxes()
			
			let vm = this

			Promise.all(promises).then(responses => {
				vm.spinner(false)

				vm.setDataFromProps()
			})
			
		},
	},
	created() {
		this.fetchData()
	}
}
</script>

<style type="text/css" scoped>

  .sparkles-icon {
	height: 1rem;
	width: 1rem;
	margin-left: -1rem;
	margin-right: 0.5rem;
  }

	.expense-total {
		box-shadow: inset 0 0 4px #efefef;
		border: 1px solid #ced4da;
		padding: 5px;
		background-color: #f5f5f5;
	}
	
	.expense-total .total-amount {
		font-size: 24pt;
		text-align: right;
	}

	.expense-line-table th {
		border-top: 1px solid #dee2e6;
	}

	.expense-line-item-static {
		cursor: pointer;
		display: none;
	}

	.expense-line-item-card, .card-delete-button {
		display: none;
	}

	.expense-line-item-card.expense-line-item-card-active {
		display: block;
	}

	.expense-line-item-static table:hover {
		background-color: #0000ff08;
	}

	.expense-line-error {
		border: 1px solid red;
	}

	@media (max-width: 1199px) {
		.expense-line-item-static  {
			display: block;
		}

		.expense-line-item-card,
		.card-delete-button  {
			display: block;
		}

		.expense-line-horizontal {
			display: none;
		}
	}

</style>